import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GROUP_CHAT_HUB_HTTP_URL } from "../../config/serviceConfig";
import MemberPanel from "./MemberPanel";

const getUserId = (currentUser) =>
  String(
    currentUser?.unified_user_id ||
      currentUser?.userId ||
      currentUser?.studentID ||
      currentUser?.student_id ||
      currentUser?.username ||
      "anonymous"
  );

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const splitProjectName = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(.*)（创建者：(.+)）$/);
  if (!match) return { title: raw, creator: "" };
  return { title: match[1].trim(), creator: match[2].trim() };
};

const GroupChatSidePanel = ({
  currentUser,
  members,
  agents,
  myUserId,
  status,
  roomId,
  activeAgentIds,
  activeStreamingAgentIds,
  keyNodes,
  activeNodeId,
  onMention,
  onSaveArchive,
  onViewArchive,
  onJumpToNode,
}) => {
  const [activeTab, setActiveTab] = useState("agents");
  const [historyRooms, setHistoryRooms] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const userId = useMemo(() => getUserId(currentUser), [currentUser]);

  const fetchHistoryRooms = useCallback(async () => {
    if (!userId || userId === "anonymous") {
      setHistoryRooms([]);
      return;
    }
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/rooms/my?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      setHistoryRooms(data.rooms || []);
    } catch (error) {
      console.error("[GroupChatSidePanel] 获取群聊历史失败:", error);
      setHistoryError(error.message || "历史会话加载失败");
      setHistoryRooms([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistoryRooms();
    }
  }, [activeTab, fetchHistoryRooms]);

  return (
    <aside className="groupchat-right-panel">
      <div className="right-panel-tabs" role="tablist" aria-label="团队协作辅助面板">
        <button
          type="button"
          className={`right-panel-tab ${activeTab === "agents" ? "active" : ""}`}
          onClick={() => setActiveTab("agents")}
        >
          成员
        </button>
        <button
          type="button"
          className={`right-panel-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          历史
        </button>
        <button
          type="button"
          className={`right-panel-tab ${activeTab === "nodes" ? "active" : ""}`}
          onClick={() => setActiveTab("nodes")}
        >
          节点
        </button>
      </div>

      <div className="right-panel-content">
        {activeTab === "agents" && (
          <MemberPanel
            members={members}
            agents={agents}
            myUserId={myUserId}
            status={status}
            onSaveArchive={onSaveArchive}
            roomId={roomId}
            onMention={onMention}
            activeAgentIds={activeAgentIds}
            activeStreamingAgentIds={activeStreamingAgentIds}
          />
        )}

        {activeTab === "history" && (
          <div className="groupchat-history-panel">
            <div className="panel-section-title">
              <span>历史会话</span>
              <button type="button" onClick={fetchHistoryRooms} disabled={historyLoading}>
                {historyLoading ? "刷新中" : "刷新"}
              </button>
            </div>
            {historyLoading && <div className="panel-empty">正在加载历史会话...</div>}
            {historyError && <div className="panel-error">{historyError}</div>}
            {!historyLoading && !historyError && historyRooms.length === 0 && (
              <div className="panel-empty">
                {userId === "anonymous" ? "请登录后查看历史会话" : "暂无历史会话"}
              </div>
            )}
            <div className="history-room-list">
              {historyRooms.slice(0, 12).map((room) => {
                const { title, creator } = splitProjectName(room.projectName || room.roomId);
                return (
                  <button
                    type="button"
                    key={room.roomId}
                    className="history-room-card"
                    onClick={() => onViewArchive?.(room.roomId, room)}
                    title="查看该团队训练记录"
                  >
                    <span className="history-room-title">{title || room.roomId}</span>
                    <span className="history-room-meta">
                      {creator ? `创建者：${creator}` : room.status || "历史"}
                      {formatTime(room.createdAt || room.updatedAt) ? ` · ${formatTime(room.createdAt || room.updatedAt)}` : ""}
                    </span>
                    <span className="history-room-summary">
                      {(room.totalMessages ?? 0) > 0 ? `${room.totalMessages} 条消息` : "点击查看记录"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "nodes" && (
          <div className="groupchat-node-panel">
            <div className="panel-section-title">
              <span>本场关键节点</span>
            </div>
            {keyNodes.length === 0 ? (
              <div className="panel-empty">暂无关键节点，阶段切换和重要结论会自动出现在这里。</div>
            ) : (
              <div className="node-timeline">
                {keyNodes.map((node, index) => (
                  <button
                    type="button"
                    key={node.id}
                    className={`node-item ${activeNodeId === node.messageDomId ? "active" : ""}`}
                    aria-current={activeNodeId === node.messageDomId ? "true" : undefined}
                    onClick={() => onJumpToNode?.(node.messageDomId)}
                  >
                    <span className="node-index">{index + 1}</span>
                    <span className="node-body">
                      <span className="node-title">{node.title}</span>
                      <span className="node-meta">{node.meta}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default GroupChatSidePanel;
